import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertOctagon, CheckCircle2, ClipboardCheck, FileEdit, Import, MessageSquareText, RefreshCw, Search, SkipForward, Wallet, Flag, History, Users, Undo2, ShieldAlert, ArrowRight } from "lucide-react";
import { api, errMsg, fmtINR, fmtDateTime, fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Skeleton } from "./ui/skeleton";
import { EmptyState, KpiCard } from "./common";
import { DemandEditDialog } from "./DemandEditDialog";
import { notifyFlagsChanged } from "../lib/flaggedCount";

/**
 * Phase 190 — Flagged Payments register shared by the Municipal Admin page, the Supervisor page (territory-only, view +
 * comments) and the Super Admin page (municipality selector). `mode` picks the API family and the dialog mode:
 *   tenant   → /t/{code}/flagged-payments…           (admin: resolve; supervisor: comment only — the API decides)
 *   platform → /platform/municipalities/{code}/flagged-payments…
 * `propertyLink(flag)` (optional) → where the PIN links to (admin / supervisor property pages).
 */
export const flagEndpoints = {
  tenant: (code) => ({ base: `/t/${code}/flagged-payments` }),
  platform: (code) => ({ base: `/platform/municipalities/${code}/flagged-payments` }),
};

const STATUS_TONE = {
  open: "bg-red-100 text-red-800 border-red-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  void: "bg-muted text-muted-foreground border-border",
};
const REASON_TONE = {
  wrong_demand: "bg-red-50 text-red-800 border-red-200",
  other: "bg-amber-50 text-amber-900 border-amber-200",
  bulk_import_excess: "bg-violet-50 text-violet-800 border-violet-200",
  advance_payment: "bg-sky-50 text-sky-800 border-sky-200",
  import_skipped: "bg-indigo-50 text-indigo-800 border-indigo-200",   // Phase 194: bulk-import row skipped in the import review (nothing recorded)
};
export const isSkippedImport = (f) => f?.reason === "import_skipped";

export const FlagStatusBadge = ({ status, labels }) => (
  <Badge variant="outline" className={`text-[11px] ${STATUS_TONE[status] || ""}`} data-testid={`flag-status-${status}`}>{labels?.[status] || status}</Badge>
);
export const FlagReasonBadge = ({ reason, label }) => (
  <Badge variant="outline" className={`text-[11px] ${REASON_TONE[reason] || ""}`}>{label || reason}</Badge>
);

export function FlaggedPaymentsPanel({ code, mode = "tenant", title, propertyLink, testId = "flagged-payments", muniName, readOnly = false }) {
  const ep = useMemo(() => (code ? flagEndpoints[mode](code) : null), [code, mode]);
  const [filters, setFilters] = useState({ status: "open", reason: "all", source: "all", pin: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [options, setOptions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [undoFlag, setUndoFlag] = useState(null);   // Undo (Municipal Admin): resolved flag whose action is being reversed

  const load = useCallback(async () => {
    if (!ep) return;
    setLoading(true); setError("");
    try {
      const params = { status: filters.status, limit: 200 };
      if (filters.reason !== "all") params.reason = filters.reason;
      if (filters.source !== "all") params.source = filters.source;
      if (filters.pin.trim()) params.pin = filters.pin.trim();
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const r = await api.get(ep.base, { params });
      setData(r.data);
    } catch (e) { setError(errMsg(e)); setData(null); } finally { setLoading(false); }
  }, [ep, filters]);
  // a comment / review / correction happened inside the drawer → refresh the table and tell the sidebar badge
  const onChanged = useCallback(() => { load(); notifyFlagsChanged(); }, [load]);

  useEffect(() => { if (ep) api.get(`${ep.base}/filters`).then((r) => setOptions(r.data)).catch(() => setOptions(null)); }, [ep]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const summary = data?.summary;
  const labels = data?.reason_labels || {};
  const statusLabels = data?.status_labels || { open: "Open", resolved: "Resolved", void: "Void" };

  return (
    <div className="space-y-4" data-testid={testId}>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Open flags" value={summary.open?.count || 0} icon={Flag} sub={muniName || "awaiting review"} testId={`${testId}-kpi-open`} />
          <KpiCard label="Excess still unreconciled" value={summary.open?.remaining || 0} money icon={AlertOctagon} sub="held as advance credit" testId={`${testId}-kpi-remaining`} />
          <KpiCard label="Resolved" value={summary.resolved?.count || 0} icon={CheckCircle2} sub={`${fmtINR(summary.resolved?.excess || 0)} reviewed / reconciled`} testId={`${testId}-kpi-resolved`} />
          <Card className="shadow-sm hover:shadow-md transition-shadow" data-testid={`${testId}-kpi-reasons`}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-muted-foreground truncate">Open by reason</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(summary.by_reason_open || {}).length === 0 ? <span className="text-sm text-muted-foreground">—</span> :
                      Object.entries(summary.by_reason_open).map(([k, x]) => (
                        <Badge key={k} variant="outline" className={`text-[11px] ${REASON_TONE[k] || ""}`} data-testid={`${testId}-kpi-reason-${k}`}>
                          <span className="font-semibold tabular-nums mr-1">{x.count}</span>{x.label}
                        </Badge>
                      ))}
                  </div>
                </div>
                <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center shrink-0"><ClipboardCheck className="h-4 w-4 text-accent-foreground" /></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="shadow-sm">
        <CardContent className="p-4">
          <form className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end" onSubmit={(e) => { e.preventDefault(); load(); }} data-testid={`${testId}-filters`}>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                <SelectTrigger className="h-9" data-testid={`${testId}-status-filter`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reason</Label>
              <Select value={filters.reason} onValueChange={(v) => setFilters((f) => ({ ...f, reason: v }))}>
                <SelectTrigger className="h-9" data-testid={`${testId}-reason-filter`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reasons</SelectItem>
                  {(options?.reasons || []).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Source</Label>
              <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
                <SelectTrigger className="h-9" data-testid={`${testId}-source-filter`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {(options?.sources || []).map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">PIN</Label>
              <Input className="h-9" placeholder="e.g. BMC100001" value={filters.pin} onChange={(e) => setFilters((f) => ({ ...f, pin: e.target.value }))} data-testid={`${testId}-pin-filter`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Paid from</Label>
              <Input type="date" className="h-9" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid={`${testId}-from-filter`} />
            </div>
            <div className="flex gap-2">
              <div className="space-y-1 flex-1">
                <Label className="text-xs">to</Label>
                <Input type="date" className="h-9" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid={`${testId}-to-filter`} />
              </div>
              <Button type="submit" size="sm" className="h-9 self-end" disabled={loading} data-testid={`${testId}-search-button`}>
                {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : error ? (
            <div className="p-6 text-center text-sm text-destructive" data-testid={`${testId}-error`}>{error} <Button variant="link" size="sm" onClick={load}>Retry</Button></div>
          ) : items.length === 0 ? (
            <EmptyState title={filters.status === "open" ? "No open flagged payments" : "No flagged payments match these filters"}
              subtitle={filters.status === "open" ? "Excess payments recorded with the reason Wrong Demand / Other, bulk-import rows above the demand due, and bulk-import rows skipped in an import review appear here until they are reconciled, imported or marked reviewed." : "Try a wider date range or another status."} />
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Paid on</TableHead>
                    <TableHead className="text-xs">Receipt</TableHead>
                    <TableHead className="text-xs">PIN / Owner</TableHead>
                    <TableHead className="text-xs hidden lg:table-cell">Area</TableHead>
                    <TableHead className="text-xs text-right">Entered</TableHead>
                    <TableHead className="text-xs text-right">Payable then</TableHead>
                    <TableHead className="text-xs text-right">Excess</TableHead>
                    <TableHead className="text-xs text-right">Still excess</TableHead>
                    <TableHead className="text-xs">Reason</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Collected by</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((f) => {
                    const skipped = isSkippedImport(f);
                    return (
                    <TableRow key={f.id} className={`cursor-pointer hover:bg-accent/40 ${skipped ? "bg-indigo-50/30" : ""}`} onClick={() => setOpenId(f.id)} data-testid={`${testId}-row-${f.id}`} data-reason={f.reason}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(f.payment_date)}</TableCell>
                      <TableCell className="text-xs font-mono">
                        {f.receipt_number}
                        {skipped && <span className="block text-[10px] font-sans text-indigo-800">{f.status === "resolved" && f.resolution?.type === "imported" ? "imported after review" : "not recorded"}</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className="font-semibold">{f.pin}</span>
                        <span className="block text-muted-foreground truncate max-w-[160px]">{f.owner_name}</span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground hidden lg:table-cell">{[f.zone_label, f.ward_label, f.mohalla_label].filter(Boolean).join(" › ") || "—"}</TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtINR(f.amount)}</TableCell>
                      {skipped ? (
                        <TableCell colSpan={3} className="text-xs text-indigo-900/80" data-testid={`${testId}-skipped-note-${f.id}`}>
                          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" aria-hidden="true" /> {f.existing_manual?.count || 0} manual receipt{(f.existing_manual?.count || 0) === 1 ? "" : "s"} kept · {fmtINR(f.existing_manual?.total || 0)}</span>
                        </TableCell>
                      ) : (<>
                        <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{fmtINR(f.payable_today)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums font-semibold text-red-700">{fmtINR(f.excess_amount)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums" data-testid={`${testId}-remaining-${f.id}`}>{f.status === "open" ? fmtINR(f.remaining_excess) : "—"}</TableCell>
                      </>)}
                      <TableCell className="text-xs"><FlagReasonBadge reason={f.reason} label={f.reason_label} /></TableCell>
                      <TableCell className="text-xs hidden md:table-cell">{f.collected_by_name || (f.source === "import" ? "Bulk import" : "—")}</TableCell>
                      <TableCell className="text-xs"><FlagStatusBadge status={f.status} labels={statusLabels} /></TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {mode === "tenant" && !readOnly && data?.can_undo && f.status === "resolved" && f.undo_kind && (
                          <Button variant="outline" size="sm" className="h-8 mr-1" onClick={(e) => { e.stopPropagation(); setUndoFlag(f); }}
                            title={`Undo "${f.undo_kind === "reviewed" ? "Mark reviewed" : "Correct demand"}"`} data-testid={`${testId}-undo-${f.id}`}>
                            <Undo2 className="h-3.5 w-3.5 mr-1" /> Undo
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); setOpenId(f.id); }} data-testid={`${testId}-open-${f.id}`}>Review</Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {data?.total > items.length && <p className="px-4 py-2 text-[11px] text-muted-foreground">Showing the first {items.length} of {data.total} — narrow the filters to see the rest.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <FlagDetailSheet code={code} mode={mode} flagId={openId} onClose={() => setOpenId(null)} onChanged={onChanged} propertyLink={propertyLink} testId={testId} readOnly={readOnly} />
      <UndoFlagDialog code={code} mode={mode} flag={undoFlag} onClose={() => setUndoFlag(null)} onDone={onChanged} testId={testId} />
    </div>
  );
}

/** Detail drawer: payment facts, demands of the property (Correct demand), comments / timeline, Mark reviewed.
 *  `readOnly` (Phase 152 view-only access) hides every action, including comments. */
export function FlagDetailSheet({ code, mode, flagId, onClose, onChanged, propertyLink, testId = "flagged-payments", readOnly = false }) {
  const ep = useMemo(() => (code ? flagEndpoints[mode](code) : null), [code, mode]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editDemand, setEditDemand] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [note, setNote] = useState("");
  const [importOpen, setImportOpen] = useState(false);   // Phase 194: "Import now" for a skipped bulk-import row
  const [importNote, setImportNote] = useState("");
  const [undoOpen, setUndoOpen] = useState(false);       // Undo (Municipal Admin) of Mark reviewed / Correct demand

  const load = useCallback(async () => {
    if (!ep || !flagId) return;
    setLoading(true);
    try { setDetail((await api.get(`${ep.base}/${flagId}`)).data); } catch (e) { toast.error(errMsg(e)); onClose(); } finally { setLoading(false); }
  }, [ep, flagId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setDetail(null); setComment(""); setNote(""); setImportNote(""); setImportOpen(false); if (flagId) load(); }, [flagId, load]);

  const f = detail?.flag;
  const skipped = isSkippedImport(f);
  const canResolve = !readOnly && !!detail?.can_resolve && f?.status === "open";
  const canUndo = mode === "tenant" && !readOnly && !!detail?.can_undo && f?.status === "resolved" && !!detail?.undo_kind;
  const canComment = !readOnly && f?.status !== "void";
  const demands = detail?.demands || [];
  const ir = f?.import_row || {};
  const manual = f?.existing_manual || { count: 0, total: 0, receipts: [] };

  const addComment = async () => {
    if (comment.trim().length < 2) return;
    setBusy(true);
    try { await api.post(`${ep.base}/${flagId}/comments`, { text: comment.trim() }); setComment(""); toast.success("Comment added"); await load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const review = async () => {
    if (note.trim().length < 5) return;
    setBusy(true);
    try {
      await api.post(`${ep.base}/${flagId}/review`, { note: note.trim() });
      toast.success("Flag marked reviewed", { description: skipped
        ? `Receipt ${f.receipt_number} stays un-imported; the manual receipts on ${f.pin} remain. Logged in the Audit Trail.`
        : `${fmtINR(f.remaining_excess)} stays as advance credit on ${f.pin}. Logged in the Audit Trail.` });
      setReviewOpen(false); await load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  // Phase 194: record the skipped file row after all — the server re-validates it and resolves the flag as "Imported after review"
  const importNow = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${ep.base}/${flagId}/import-now`, { note: importNote.trim() || null });
      const pay = r.data?.payment || {};
      toast.success("Receipt imported", { description: `${pay.receipt_number || f.receipt_number} · ${fmtINR(pay.amount ?? f.amount)} recorded on ${f.pin}. The flag is resolved and logged in the Audit Trail.` });
      setImportOpen(false); await load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onCorrected = async (res) => {
    const st = res?.flag?.status;
    if (st === "resolved") toast.success("Flag resolved — the payment now fits the corrected demand", { description: "Resolution logged in the Audit Trail with your name, the time and your reason." });
    else if (res?.flag) toast.info(`Flag stays open — ${fmtINR(res.flag.remaining_excess)} is still above what the property owes`);
    await load(); onChanged?.();
  };

  return (
    <Sheet open={!!flagId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid={`${testId}-detail`}>
        {!f ? (
          <div className="space-y-3 pt-6">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading flex items-center gap-2 flex-wrap">
                {skipped ? "Skipped import receipt" : "Flagged payment"} · {f.receipt_number}
                <FlagStatusBadge status={f.status} labels={detail.status_labels} />
                <FlagReasonBadge reason={f.reason} label={f.reason_label} />
              </SheetTitle>
              <SheetDescription>
                {propertyLink ? <Link to={propertyLink(f)} className="font-semibold text-primary underline-offset-2 hover:underline" data-testid={`${testId}-detail-pin`}>{f.pin}</Link> : <span className="font-semibold" data-testid={`${testId}-detail-pin`}>{f.pin}</span>}
                {" · "}{f.owner_name} · {[f.zone_label, f.ward_label, f.mohalla_label].filter(Boolean).join(" › ")}
              </SheetDescription>
            </SheetHeader>

            {skipped ? (   /* Phase 194: nothing was recorded — show the file row against the manual receipts that were kept */
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-sm">
                  {[["File amount", fmtINR(f.amount)], ["File date", fmtDate(f.payment_date)], ["Mode", f.payment_mode || ir.mode_text || "—"], ["Manual receipts kept", `${manual.count} · ${fmtINR(manual.total)}`]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border px-3 py-2">
                      <p className="text-[11px] text-muted-foreground">{l}</p>
                      <p className="font-semibold tabular-nums" data-testid={`${testId}-detail-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2" data-testid={`${testId}-detail-skipped-meta`}>
                  Bulk-import row {ir.row_number || "—"} of <i>{f.import_file || "the import file"}</i> · skipped {fmtDateTime(f.created_at)}{f.created_by?.name ? ` by ${f.created_by.name}` : ""} because {f.pin} already had manual receipts.
                  {f.comment ? <> · {f.comment}</> : null}
                </p>
                {f.status === "open" && (
                  <p className="text-xs rounded-md border border-indigo-200 bg-indigo-50 text-indigo-900 px-3 py-2 mt-2 flex gap-2" data-testid={`${testId}-detail-skipped-hint`}><SkipForward className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>This receipt was <b>not recorded</b>. Use <b>Import now</b> to record it as an imported payment (the manual receipts stay), or <b>Mark reviewed</b> to close it without importing.</span></p>
                )}
                {f.resolution && (
                  <p className="text-xs rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 mt-2" data-testid={`${testId}-detail-resolution`}>
                    <b>{f.resolution.label}</b>{f.resolution.by?.name ? ` by ${f.resolution.by.name}` : ""} · {fmtDateTime(f.resolution.at)}
                    {f.resolution.receipt_number ? <> · receipt <span className="font-mono">{f.resolution.receipt_number}</span></> : null}
                    {f.resolution.note ? <> · {f.resolution.note}</> : null}
                  </p>
                )}
                <div className="mt-5" data-testid={`${testId}-detail-manual`}>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /> Manual receipts kept on {f.pin}
                    <Badge variant="outline" className="text-[10px] tabular-nums">{manual.count} · {fmtINR(manual.total)}</Badge></h3>
                  <div className="rounded-lg border overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead className="text-xs">Receipt</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs">Mode</TableHead>
                        <TableHead className="text-xs">Collected by</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {(manual.receipts || []).map((r, i) => (
                          <TableRow key={r.payment_id || i} data-testid={`${testId}-detail-manual-${i}`}>
                            <TableCell className="text-xs font-mono">{r.receipt_number || "—"}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">{fmtDate(r.payment_date)}</TableCell>
                            <TableCell className="text-xs text-right tabular-nums">{fmtINR(r.amount)}</TableCell>
                            <TableCell className="text-xs">{r.payment_mode || "—"}</TableCell>
                            <TableCell className="text-xs">{r.collected_by_name || "—"}{r.collected_by_employee_id ? <span className="text-muted-foreground"> ({r.collected_by_employee_id})</span> : null}</TableCell>
                          </TableRow>
                        ))}
                        {(manual.receipts || []).length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-4">No snapshot of the manual receipts</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">Snapshot taken when the import was reviewed — the receipts may have changed since.</p>
                </div>
              </>
            ) : (
              <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-sm">
              {[["Entered", f.amount], ["Payable then", f.payable_today], ["Excess", f.excess_amount], ["Still excess", f.status === "open" ? f.remaining_excess : 0]].map(([l, v], i) => (
                <div key={l} className={`rounded-lg border px-3 py-2 ${i === 3 && f.status === "open" && v > 0 ? "border-red-200 bg-red-50/60" : ""}`}>
                  <p className="text-[11px] text-muted-foreground">{l}</p>
                  <p className="font-semibold tabular-nums" data-testid={`${testId}-detail-${l.toLowerCase().replace(/\s+/g, "-")}`}>{fmtINR(v)}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Paid {fmtDate(f.payment_date)} via {f.payment_mode || "—"} · {f.source === "import" ? "bulk import" : `collected by ${f.collected_by_name || "—"}`} · flagged {fmtDateTime(f.created_at)}{f.created_by?.name ? ` by ${f.created_by.name}` : ""}
              {f.comment ? <> · Comment: <i>{f.comment}</i></> : null}
            </p>
            {f.status === "open" && f.remaining_excess > 0.004 && (
              <p className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 mt-2 flex gap-2"><Wallet className="h-4 w-4 shrink-0 mt-0.5" />
                {fmtINR(f.remaining_excess)} is held as advance credit on this property. Correct the demand and the money is settled against it automatically; or mark the flag reviewed to keep it as credit.</p>
            )}
            {f.resolution && (
              <p className="text-xs rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2 mt-2" data-testid={`${testId}-detail-resolution`}>
                <b>{f.resolution.label}</b>{f.resolution.by?.name ? ` by ${f.resolution.by.name}` : ""} · {fmtDateTime(f.resolution.at)}{f.resolution.note ? <> · {f.resolution.note}</> : null}
              </p>
            )}
            {canUndo && (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900" data-testid={`${testId}-detail-undo-hint`}>
                <span>Closed by <b>{detail.undo_kind === "reviewed" ? "Mark reviewed (keep as credit)" : "Correct demand"}</b>. You can reverse this action; the flag will be Open again.</span>
                <Button size="sm" variant="outline" className="h-7 shrink-0" onClick={() => setUndoOpen(true)} data-testid={`${testId}-detail-undo-button`}><Undo2 className="h-3.5 w-3.5 mr-1" /> Undo</Button>
              </div>
            )}
              </>
            )}

            {/* ---- demands (Admin / Super Admin: Correct demand) ---- */}
            <div className="mt-5">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><FileEdit className="h-4 w-4 text-muted-foreground" /> Demands on {f.pin}</h3>
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="text-xs">Head · FY</TableHead>
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                    <TableHead className="text-xs text-right">Balance</TableHead>
                    {canResolve && <TableHead className="text-xs text-right"></TableHead>}
                  </TableRow></TableHeader>
                  <TableBody>
                    {demands.map((d) => (
                      <TableRow key={d.id} data-testid={`${testId}-demand-${d.id}`}>
                        <TableCell className="text-xs">{d.tax_head_name} <span className="text-muted-foreground">{d.fy_label}</span>{d.correction_count > 0 && <Badge variant="outline" className="ml-1 text-[10px]">Corrected ×{d.correction_count}</Badge>}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmtINR(d.total_demand)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmtINR(d.paid_amount)}</TableCell>
                        <TableCell className="text-xs text-right tabular-nums">{fmtINR(d.balance_amount)}</TableCell>
                        {canResolve && <TableCell className="text-right"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditDemand(d)} data-testid={`${testId}-correct-${d.id}`}>Correct demand</Button></TableCell>}
                      </TableRow>
                    ))}
                    {demands.length === 0 && <TableRow><TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-4">No demands on this property</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
              {detail.property && <p className="text-[11px] text-muted-foreground mt-1">Advance credit on property now: <b className="tabular-nums" data-testid={`${testId}-detail-credit`}>{fmtINR(detail.property.advance_credit)}</b></p>}
            </div>

            {/* ---- actions ---- */}
            {canResolve && (
              <div className="mt-4 flex flex-wrap gap-2">
                {skipped && (
                  <Button onClick={() => setImportOpen(true)} data-testid={`${testId}-import-now-button`}><Import className="h-4 w-4 mr-1.5" /> Import now</Button>
                )}
                <Button variant="outline" onClick={() => setReviewOpen(true)} data-testid={`${testId}-review-button`}>
                  <ClipboardCheck className="h-4 w-4 mr-1.5" /> {skipped ? "Mark reviewed (keep manual receipts)" : "Mark reviewed (keep as credit)"}
                </Button>
              </div>
            )}

            {/* ---- comments + timeline ---- */}
            <div className="mt-5">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><History className="h-4 w-4 text-muted-foreground" /> History & comments</h3>
              <ol className="space-y-2" data-testid={`${testId}-timeline`}>
                {(f.timeline || []).slice().reverse().map((t) => (
                  <li key={t.id} className="text-xs rounded-md border px-3 py-2">
                    <p className="text-muted-foreground">{fmtDateTime(t.at)}{t.by?.name ? ` · ${t.by.name}${t.by.role_label ? ` (${t.by.role_label})` : ""}` : " · System"} · {t.type}</p>
                    <p className="mt-0.5">{t.text}</p>
                  </li>
                ))}
              </ol>
              {canComment && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs flex items-center gap-1"><MessageSquareText className="h-3.5 w-3.5" /> Add a comment</Label>
                  <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What did you check / find?" data-testid={`${testId}-comment-input`} />
                  <Button size="sm" onClick={addComment} disabled={busy || comment.trim().length < 2} data-testid={`${testId}-comment-button`}>Add comment</Button>
                </div>
              )}
            </div>

            <DemandEditDialog code={code} demand={editDemand} pin={f.pin} open={!!editDemand} onOpenChange={(o) => !o && setEditDemand(null)}
              mode={mode} extraBody={{ flag_id: f.id }} onSaved={onCorrected} />

            <UndoFlagDialog code={code} mode={mode} flag={undoOpen ? f : null} onClose={() => setUndoOpen(false)}
              onDone={async () => { await load(); onChanged?.(); }} testId={`${testId}-detail`} />

            <Dialog open={reviewOpen} onOpenChange={(o) => !busy && setReviewOpen(o)}>
              <DialogContent data-testid={`${testId}-review-dialog`}>
                <DialogHeader>
                  <DialogTitle className="font-heading">{skipped ? "Mark reviewed — keep the manual receipts" : "Mark reviewed — keep the excess as credit"}</DialogTitle>
                  <DialogDescription>
                    {skipped
                      ? <>Receipt <span className="font-mono">{f.receipt_number}</span> ({fmtINR(f.amount)}) stays <b>un-imported</b> and the {manual.count} manual receipt{manual.count === 1 ? "" : "s"} on {f.pin} remain as they are. A note is mandatory; it is written to the Audit Trail with your name and the time.</>
                      : <>The demand stays as it is and {fmtINR(f.remaining_excess)} remains advance credit on {f.pin} (applied automatically to the property's next demand). A note is mandatory; it is written to the Audit Trail with your name and the time.</>}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-1">
                  <Label htmlFor="flag-review-note" className="text-xs">Note *</Label>
                  <Textarea id="flag-review-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={skipped ? "e.g. The manual receipt already covers this payment; the file row is a duplicate." : "e.g. Owner paid ahead for next year on purpose; demand is correct."} data-testid={`${testId}-review-note`} />
                  {note.trim().length > 0 && note.trim().length < 5 && <p className="text-[11px] text-destructive">At least 5 characters.</p>}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={busy}>Cancel</Button>
                  <Button onClick={review} disabled={busy || note.trim().length < 5} data-testid={`${testId}-review-submit`}>{busy ? "Saving…" : "Mark reviewed"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Phase 194: Import now — record the skipped bulk-import row after all */}
            <Dialog open={importOpen} onOpenChange={(o) => !busy && setImportOpen(o)}>
              <DialogContent data-testid={`${testId}-import-now-dialog`}>
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2"><Import className="h-4 w-4" /> Import this receipt now</DialogTitle>
                  <DialogDescription>
                    Receipt <span className="font-mono">{f.receipt_number}</span> · {fmtINR(f.amount)} · {fmtDate(f.payment_date)} · {f.payment_mode || ir.mode_text || "—"} will be recorded on <b>{f.pin}</b> as an imported payment,
                    exactly as the file row would have been. The {manual.count} manual receipt{manual.count === 1 ? "" : "s"} ({fmtINR(manual.total)}) stay untouched — if the money now exceeds what is due, the rest becomes advance credit.
                    The row is re-validated first; if it collides with an existing receipt you will be told and nothing is written.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-1">
                  <Label htmlFor="flag-import-note" className="text-xs">Note (optional)</Label>
                  <Textarea id="flag-import-note" rows={2} value={importNote} onChange={(e) => setImportNote(e.target.value)} placeholder="e.g. Owner confirmed both payments are genuine." data-testid={`${testId}-import-now-note`} />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setImportOpen(false)} disabled={busy}>Cancel</Button>
                  <Button onClick={importNow} disabled={busy} data-testid={`${testId}-import-now-submit`}>{busy ? "Importing…" : `Import ${fmtINR(f.amount)} now`}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        {loading && f && <p className="text-[11px] text-muted-foreground mt-2">Refreshing…</p>}
      </SheetContent>
    </Sheet>
  );
}

/** Undo (Municipal Admin, 2026-10-25): preview of what will change → mandatory note → confirm. The server refuses with a
 *  plain-words reason (and what to do instead) when the money has been used since the action. */
export function UndoFlagDialog({ code, mode = "tenant", flag, onClose, onDone, testId = "flagged-payments" }) {
  const ep = useMemo(() => (code ? flagEndpoints[mode](code) : null), [code, mode]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const open = !!flag;
  useEffect(() => {
    if (!open || !ep) return;
    setPreview(null); setNote(""); setError(""); setLoading(true);
    api.get(`${ep.base}/${flag.id}/undo-preview`).then((r) => setPreview(r.data)).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  }, [open, ep, flag?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (note.trim().length < 5 || !preview?.can_undo) return;
    setBusy(true);
    try {
      const r = await api.post(`${ep.base}/${flag.id}/undo`, { note: note.trim() });
      const d = r.data || {};
      toast.success(d.reopened ? "Undone — the flag is Open again" : "Undone — the demand was restored", {
        description: d.reopened ? `${fmtINR(d.remaining_excess)} on ${flag.pin} is shown as unreconciled again. Logged in the Audit Trail with your name, the time and your note.`
          : `The receipt still fits what ${flag.pin} owes, so the flag stays resolved. Logged in the Audit Trail.` });
      onClose(); onDone?.();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };
  const ch = preview?.changes;
  const dem = ch?.demand;
  const Row = ({ label, now, after, money = true, testKey }) => (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 text-xs py-1 border-b last:border-b-0" data-testid={`${testId}-undo-row-${testKey}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{money ? fmtINR(now) : now}</span>
      <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
      <span className="tabular-nums font-semibold">{money ? fmtINR(after) : after}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid={`${testId}-undo-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Undo2 className="h-4 w-4" /> Undo {preview?.kind_label ? `"${preview.kind_label}"` : "this action"}</DialogTitle>
          <DialogDescription>
            Receipt <span className="font-mono">{flag?.receipt_number}</span> · <b>{flag?.pin}</b>{flag?.owner_name ? ` · ${flag.owner_name}` : ""} · {fmtINR(flag?.amount)} entered
            {preview?.resolution?.by ? <> — closed by {preview.resolution.by} on {fmtDateTime(preview.resolution.at)}</> : null}
          </DialogDescription>
        </DialogHeader>
        {loading && <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>}
        {!loading && error && !preview && <p className="text-sm text-destructive" data-testid={`${testId}-undo-error`}>{error}</p>}
        {!loading && preview && !preview.can_undo && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-950 px-3 py-3 text-sm space-y-2" data-testid={`${testId}-undo-blocked`}>
            <p className="font-semibold flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> This cannot be undone</p>
            <ul className="list-disc pl-5 space-y-1 text-xs">{preview.blockers.map((b, i) => <li key={i} data-testid={`${testId}-undo-blocker-${i}`}>{b}</li>)}</ul>
            {preview.suggestion && <p className="text-xs border-t border-amber-200 pt-2"><b>What you can do:</b> {preview.suggestion}</p>}
          </div>
        )}
        {!loading && preview?.can_undo && (
          <div className="space-y-3">
            <div className="rounded-lg border px-3 py-2" data-testid={`${testId}-undo-summary`}>
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase tracking-wide text-muted-foreground pb-1 border-b">
                <span>What changes</span><span>Now</span><span /><span>After undo</span>
              </div>
              <Row label="Flag status" now={preview.status === "resolved" ? "Resolved" : preview.status} after="Open" money={false} testKey="status" />
              <Row label="Unreconciled excess shown" now={ch.remaining_excess_now} after={ch.remaining_excess_after} testKey="excess" />
              <Row label={`Advance credit on ${preview.pin}`} now={ch.credit_now} after={ch.credit_after} testKey="credit" />
              {dem && (<>
                <Row label={`Demand · ${dem.head}`} now={dem.now.total_demand} after={dem.after_undo.total_demand} testKey="demand" />
                {(Math.abs(dem.now.due - dem.now.total_demand) > 0.004 || Math.abs(dem.after_undo.due - dem.after_undo.total_demand) > 0.004) && (
                  <Row label="Due (with penalty / rebate)" now={dem.now.due} after={dem.after_undo.due} testKey="due" />)}
                <Row label="Paid on this demand" now={dem.paid_now} after={dem.paid_after_undo} testKey="paid" />
                <Row label="Balance on this demand" now={dem.balance_now} after={dem.balance_after_undo} testKey="balance" />
              </>)}
              {ch.note && <p className="text-[11px] text-muted-foreground pt-2">{ch.note}</p>}
            </div>
            {preview.warnings?.length > 0 && (
              <ul className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs list-disc pl-7 space-y-1" data-testid={`${testId}-undo-warnings`}>
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <div className="space-y-1">
              <Label htmlFor="flag-undo-note" className="text-xs">Why are you undoing this? *</Label>
              <Textarea id="flag-undo-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Marked reviewed by mistake — the owner disputes the demand, it must be checked again." data-testid={`${testId}-undo-note`} />
              {note.trim().length > 0 && note.trim().length < 5 && <p className="text-[11px] text-destructive">At least 5 characters.</p>}
              <p className="text-[11px] text-muted-foreground">Written to the Audit Trail with your name, the date and time, this note and what was reversed.</p>
            </div>
            {error && <p className="text-sm text-destructive" data-testid={`${testId}-undo-error`}>{error}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid={`${testId}-undo-cancel`}>{preview && !preview.can_undo ? "Close" : "Cancel"}</Button>
          {preview?.can_undo && (
            <Button onClick={submit} disabled={busy || note.trim().length < 5} data-testid={`${testId}-undo-confirm`}>
              {busy ? "Undoing…" : <><Undo2 className="h-4 w-4 mr-1.5" /> Confirm undo</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

